import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, EMPTY_STRING, noop } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type {
  FieldFields,
  FieldInstanceFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import { RegrelloIcon, type RegrelloIconName, type RegrelloIconProps } from "@regrello/ui-core";
import type { ColDef, ValueFormatterParams, ValueParserParams } from "ag-grid-community";
import debounce from "lodash/debounce";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import { ValidationRules } from "../../../../../../constants/globalConstants";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { RowDatum } from "../../../../../../ui2/forms/instance/utils/tableV2Utils";
import { showToast } from "../../../../components/toast/useToastMessageQueue";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import {
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloControlledFormFieldText } from "../../../formFields/controlled/RegrelloControlledFormFieldText";
import { RegrelloFormFieldText } from "../../../formFields/RegrelloFormFieldText";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import { FrontendValueConstraintRuleName, validateByConstraints } from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellEmail } from "./_internal/RegrelloEditableCellEmail";
import type { EmailFieldPluginFrontendValue } from "./_internal/types";

type VALUE = EmailFieldPluginFrontendValue; // Saves some characters without using a troublesome generic type.

/**
 * Debounce the `showToast` function to avoid showing too many toast messages in rapid succession,
 * particularly on bulk paste.
 *
 * Also, we're showing on the leading end of the delay to give immediate feedback even if there are
 * lots and lots of cells being pasted at once.
 *
 * @see showToast
 */
const debouncedShowToast = debounce(showToast, 100, { leading: true, trailing: false });

export class SpectrumEmailFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<VALUE>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.email";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.EMAIL;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "STRING");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.EMAIL,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(valueConstraints: SpectrumValueConstraintFields[]) {
    return valueConstraints.filter(
      ({ valueConstraintRule }) =>
        valueConstraintRule === FrontendValueConstraintRuleName.MAX_CHARACTER ||
        valueConstraintRule === FrontendValueConstraintRuleName.MIN_CHARACTER,
    );
  }

  public override getFieldDisplayName = () => {
    return t`Email`;
  };

  public override getIconName = (): RegrelloIconName => {
    return "email";
  };

  public override renderIcon = (props?: Omit<RegrelloIconProps, "iconName">) => {
    return <RegrelloIcon {...props} iconName="email" />;
  };

  public getConstraintConditionOperators() {
    return {
      IS_ZERO: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      IS_NOT_ZERO: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      EQ: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      NOT: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_CONTAINS: {
        label: t`Contains`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_NOT_CONTAINS: {
        label: t`Does not contain`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_STARTS_WITH: {
        label: t`Starts with`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_ENDS_WITH: {
        label: t`Ends with`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onBlur" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    return field.fieldConstraints;
  }

  public renderPreviewFormField() {
    return <RegrelloFormFieldText disabled={true} onChange={noop} size="medium" value="" />;
  }

  public renderValueConstraints(_props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return null;
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    _field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    const isDeleted = props.fieldInstance?.field.deletedAt != null;
    return (
      <RegrelloControlledFormFieldText
        {...props}
        key={props.controllerProps.name}
        controllerProps={{
          ...props.controllerProps,
          rules: { ...props.controllerProps.rules, ...ValidationRules.VALID_OR_EMPTY_EMAIL },
        }}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        infoTooltipText={props.description ?? undefined}
        isDeleted={isDeleted}
      />
    );
  };

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const normalizedValue = value.trim();

    const result = validateRequired(normalizedValue, options.isRequired);
    if (result != null) {
      return result;
    }

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, normalizedValue);
    if (!spectrumValidationResult.isValid) {
      return {
        value: normalizedValue,
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return { value: normalizedValue };
  }

  public override parseTableValue(_spectrumFieldVersion: SpectrumFieldVersionFields, valueIn: unknown) {
    return typeof valueIn === "string" ? valueIn : EMPTY_STRING;
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, VALUE>>,
    options: {
      fieldInstance: FieldInstanceFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => string;
    },
  ): ColDef<TData, VALUE> {
    return {
      ...colDefParams,
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "rg-grid-not-editable": (params) => {
          // (max): gray cell out if it is not editable.
          if (params.colDef.editable == null) {
            return true;
          }
          if (typeof params.colDef.editable === "boolean") {
            return !params.colDef.editable;
          }
          return !params.colDef.editable(params);
        },
      },
      cellDataType: "text",
      cellEditor: RegrelloEditableCellEmail,
      cellEditorParams: {
        clearValueIfInvalid: this.clearValueIfInvalid,
        fieldInstance: options.fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      valueParser: (params: ValueParserParams<TData, VALUE>) => {
        const parsedValue = this.parseTableValue(options.fieldInstance.spectrumFieldVersion, params.newValue);
        const validOrEmptyValue = this.clearValueIfInvalid(parsedValue);
        if (validOrEmptyValue !== parsedValue) {
          debouncedShowToast({
            content: t`Invalid email addresses were removed`,
            intent: "warning",
          });
        }
        return validOrEmptyValue;
      },
      cellRenderer: (params: ValueFormatterParams<TData, VALUE>): ReactNode => {
        if (params.value == null || params.value === "") {
          return options.placeholderRenderer ? options.placeholderRenderer(params) : "";
        }
        return String(params.value);
      },
    };
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: EmailFieldPluginFrontendValue,
  ): UpdateFieldInstanceValueInputs {
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      stringValue: frontendValue,
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    if (value == null || value === EMPTY_STRING) {
      if (isRequired) {
        return { isValid: false, errors: [t`This field is required`] };
      }
      return { isValid: true, errors: EMPTY_ARRAY };
    }

    if (typeof value !== "string") {
      return { isValid: false, errors: [t`Must be a string or null`] };
    }

    return this.validateValueInternal(fieldVersion, value);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    const result = ValidationRules.VALID_EMAIL.validate(value);
    if (result !== true) {
      return { isValid: false, errors: [result] };
    }

    return validateByConstraints(fieldVersion.fieldConstraints, value);
  }

  /**
   * Clear the provided email value if it is invalid. Used to enforce validation on blur in Tables Alpha.
   *
   * See: https://regrello.slack.com/archives/C05NGEBLE1E/p1759863355149869
   */
  // (clewis): Define as an arrow function to avoid 'this' issues when passing to cellEditorParams.
  private clearValueIfInvalid = (value: VALUE) => {
    const trimmedValue = value.trim();
    const validationResult = ValidationRules.VALID_OR_EMPTY_EMAIL.validate(trimmedValue);
    if (typeof validationResult === "string") {
      return this.getEmptyValueForFrontend();
    }
    return value;
  };
}
